import { useEffect, useState } from "react";
import { apiClient } from "../lib/http/apiClient";

/**
 * Fetches a presigned S3 URL through our own backend (`GET /rag/download-proxy`)
 * instead of the browser talking to S3 directly. Presigned URLs fetched
 * client-side fail whenever the bucket's CORS allow-list doesn't include the
 * app's current origin — the fetch throws, and any naive fallback to
 * `window.open(rawUrl)` ends up navigating the user straight to the raw S3
 * URL (full of AWS credentials/signature query params), which is exactly
 * what we don't want anyone to see. Going through our own API sidesteps
 * bucket CORS entirely: the browser only ever talks to our own origin.
 */
async function fetchViaProxy(sourceUrl: string, headers?: Record<string, string | undefined>): Promise<Blob> {
  const res = await apiClient.get<Blob>("/rag/download-proxy", {
    params: { url: sourceUrl },
    responseType: "blob",
    headers,
  });
  return res.data;
}

/** Loads a presigned S3 URL as a same-origin `blob:` object URL via the
 * download-proxy above, so the URL never appears anywhere a user could see
 * it — not in a new tab's address bar, not via "copy image address".
 *
 * `headers` is needed by admin-only callers: the download-proxy call goes
 * through the normal axios interceptor, which only ever attaches the
 * regular buyer/seller session token, never the separate admin token — so
 * an admin viewing a document without also being logged in as a regular
 * user gets a silent 401 unless the admin token is passed explicitly here. */
export function useBlobUrl(sourceUrl: string | null | undefined, headers?: Record<string, string | undefined>): string | null {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!sourceUrl) {
      setBlobUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setBlobUrl(null);

    fetchViaProxy(sourceUrl, headers)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        // Left null — callers fall back to their own empty/loading state
        // rather than a broken blob URL.
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [sourceUrl]);

  return blobUrl;
}

/** Opens a presigned S3 URL in a new tab via the download-proxy + a blob
 * object URL, so the raw S3 URL never appears in the new tab's address bar.
 * On failure, shows nothing rather than falling back to the raw URL —
 * exposing it defeats the entire point of this helper. */
export async function openAsBlob(sourceUrl: string, headers?: Record<string, string | undefined>): Promise<void> {
  try {
    const blob = await fetchViaProxy(sourceUrl, headers);
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch {
    // Intentionally no fallback to the raw URL here — see module docstring.
  }
}
